import React, { useEffect, useState } from "react";
import { getPermissions } from "../rbac/hasAccess";
import { useLocation, useNavigate } from "react-router-dom";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { formateDate, paginationrowsPerPageOptions } from "../../../utils/commonComponent/commonConstant";
import { textTruncate } from "../../../utils/commonComponent/truncate";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import ListSearchSection from "../../../utils/Custom/ListSearchSection";
import { AddButton } from "../../../utils/Custom/Button";
import { submitSiteDelete } from "../../../utils/commonComponent/delete";
import { Tooltip } from "@mui/material";

const headers = [ "id", "customerName", "enquiryNo",];

const fields = [
  { value: "customerName", label: "Client" },
  { value: "enquiryNo", label: "Enquiry No" },
];

const EnquiryList = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const permissions = getPermissions(props.moduleId);

  const handleEdit = (row) => navigate(`/editEnquiry/${row.id}`);
  const followUpEnquiry = (row) => navigate(`/enquiryFollowUp/${row.id}`);

  const handleDelete = (row) => submitSiteDelete("enquiry", row.id);

  const [enquiryList, setEnquiryList] = React.useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [searchBy, setSearchBy] = React.useState(sessionStorage.getItem("searchBy") || "Client");
  const [searchKey, setSearchKey] = React.useState(sessionStorage.getItem("searchKey") || "");
  const [searched, setSearched] = React.useState(sessionStorage.getItem("searched") === "true" || false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(() =>Number(sessionStorage.getItem("ListPageCount") || 0));
  const [rowsPerPage, setRowsPerPage] = useState(() =>Number(sessionStorage.getItem("ListRowsPerPage") || 30));

  const handlePaginationChange = (page, rows) => {
    setCurrentPage(page);
    setRowsPerPage(rows);
    // fetch new data from backend here
    sessionStorage.setItem("ListPageCount", page);
    sessionStorage.setItem("ListRowsPerPage", rows);
  };

  const getWhereClause = () => {
    let whereConditions = "";
    const colMap = {
      Client: "e.customerName",
      enquiryNo: "e.enquiryNo",
    };
    const colName = colMap[searchBy];
    if (colName && searchKey.trim()) {
      whereConditions = `WHERE LOWER(${colName}) LIKE LOWER('%${searchKey.trim()}%')`;
    }

    return whereConditions;
  };

  const getDataQuery = () => {
    const whereClause = getWhereClause();
    let paginationSettings = "";
    if (rowsPerPage !== -1) {
      paginationSettings = `LIMIT ${rowsPerPage} OFFSET ${
        rowsPerPage * Number(currentPage)
      }`;
    }
    return `SELECT e.*, COUNT(q.id) AS quotationCount FROM enquiry e LEFT JOIN quotation q ON q.enquiryId = e.id ${whereClause} GROUP BY e.id ORDER BY e.id DESC ${paginationSettings};`;
  };

  const getCountQuery = () => {
    const whereClause = getWhereClause();
    return `SELECT COUNT(*) AS totalRows FROM enquiry e LEFT JOIN quotation q ON q.enquiryId = e.id ${whereClause};`;
  };

  const fetchData = async () => {
    try {
      const [enquiryList, countResult] = await Promise.all([
        fetchQuery(getDataQuery()),
        fetchQuery(getCountQuery()),
      ]);
      let modifiedData = enquiryList?.map((item) => {
        return {
          ...item,
          Items: item.subcategory || "",
        };
      });
      setEnquiryList(modifiedData);
      setTotalRows(countResult?.[0]?.totalRows || 0);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  const handleSearch = () => {
    if (!searchBy || !searchKey.trim()) {
      alert("Please select a search field and enter a search value.");
      return;
    }
    fetchData();
    setSearched(true);
    // Save search state to sessionStorage
    sessionStorage.setItem("searchBy", searchBy);
    sessionStorage.setItem("searchKey", searchKey);
    sessionStorage.setItem("searched", "true");
    sessionStorage.setItem("currentPath", location.pathname);
    sessionStorage.setItem("ListPageCount", 0);
    setCurrentPage(0);
  };

  const clearSearch = () => {
    setSearched(false);
    setSearchBy("Client");
    setSearchKey("");
    // Clear all employee list session storage
    sessionStorage.removeItem("ListPageCount");
    sessionStorage.removeItem("ListRowsPerPage");
    sessionStorage.removeItem("searchBy");
    sessionStorage.removeItem("searchKey");
    sessionStorage.removeItem("searched");
    setCurrentPage(0);
  };

  useEffect(() => {
    if (searched == false) {
      fetchData();
    }
  }, [searched]);

  return (
    <>
      {(permissions.create || permissions.allAccess) && (
        <AddButton
          id="btn-add-outline"
          to="/editEnquiry/0"
          label="Add Enquiry"
          icon={<AddIcon fontSize="small" />}
        />
      )}

      <ListSearchSection
        title="Search Enquiry"
        fields={fields}
        onSearch={handleSearch}
        searchKey={searchKey}
        setSearchKey={setSearchKey}
        searchBy={searchBy}
        setSearchBy={setSearchBy}
        clearSearch={clearSearch}
        searched={searched}
        setSearched={setSearched}
      />
      <br />

      <div className="w-full border rounded-md shadow">
        <div className="overflow-y-auto h-auto max-h-[550px]">
          <table className="min-w-full text-sm text-center text-gray-700">
            <thead className="sticky top-0 bg-blue-50 text-xs font-medium text-gray-600 z-10">
              <tr>
                <th className="px-4 py-3 whitespace-nowrap">Sr. No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Enquiry No</th>
                <th className="px-4 py-3 whitespace-nowrap">Client</th>
                <th className="px-4 py-3 whitespace-nowrap">Contact Person</th>
                <th className="px-4 py-3 whitespace-nowrap">Email</th>
                <th className="px-4 py-3 whitespace-nowrap">Contact No.</th>
                <th className="px-4 py-3 whitespace-nowrap">Enquiry Date</th>
                {(permissions.view || permissions.edit || permissions.delete || permissions.allAccess) && (
                  <th className="px-4 py-3 whitespace-nowrap">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {enquiryList?.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="px-4 py-3">
                    {currentPage * rowsPerPage + rowIndex + 1}
                  </td>
                  <td className="px-4 py-3">{row.enquiryNo ?? "-"}</td>
                  <td className="px-4 py-3 cursor-pointer hover:text-blue-600">{textTruncate(row.customerName) ?? "-"}</td>
                  <td className="px-4 py-3">{row.contactPerson ?? "-"}</td>
                  <td className="px-4 py-3">{row.customer_email_id ?? "-"}</td>
                  <td className="px-4 py-3">{row.customer_contact_number ?? "-"}</td>
                  <td className="px-4 py-3">{formateDate(row.enquiryDate) ?? "-"}</td>
                  <td className="px-4 py-2 text-center whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      {(permissions.edit || permissions.allAccess) && (
                        <Tooltip title="Edit" placement="top-start">
                          <button
                            onClick={() => handleEdit(row)}
                            className="text-blue-600"
                          >
                            <EditIcon fontSize="small" />
                          </button>
                        </Tooltip>
                      )}
                      {(permissions.edit || permissions.allAccess) && (
                        <Tooltip title="Enquiry FollowUp" placement="top-start">
                          <button
                            onClick={() => followUpEnquiry(row)}
                            className="text-blue-600"
                          >
                            <BookmarkIcon fontSize="small" />
                          </button>
                        </Tooltip>
                      )}
                      {(permissions.edit || permissions.allAccess) && (
                        <Tooltip
                          title={row.quotationCount > 0 ? "Quotation Already Created" : "Convert to Quotation"}
                          placement="top-start"
                        >
                          <span>
                            <button
                              onClick={() => {
                                if(window.confirm("Are you sure to convert Enquiry to Quotation?")){
                                  navigate(`/quotation/0?fromEnquiry=${row.id}`);
                                }
                              }}
                              className="text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                              disabled={row.quotationCount > 0}
                            >
                              <ReceiptLongIcon fontSize="small" />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                      {(permissions.delete || permissions.allAccess) && (
                        <Tooltip title="Delete" placement="top-start">
                          <button
                            onClick={() => handleDelete(row)}
                            className="text-blue-600"
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {enquiryList.length === 0 && (
                <tr>
                  <td
                    colSpan={headers.length + 1}
                    className="text-center py-4 text-gray-500"
                  >
                    No data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="flex items-center justify-between px-4 py-2 bg-blue-50 text-sm border-t">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={rowsPerPage}
              onChange={(e) =>
                handlePaginationChange(0, Number(e.target.value))
              }
              className="border rounded px-2 py-1 text-sm"
            >
              {paginationrowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-gray-600">
              {totalRows === 0
                ? "0–0 of 0"
                : `${currentPage * rowsPerPage + 1}–${Math.min(
                    (currentPage + 1) * rowsPerPage,
                    totalRows
                  )} of ${totalRows}`}
            </span>
            <button
              onClick={() => handlePaginationChange(0, rowsPerPage)}
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <FirstPageIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(currentPage - 1, rowsPerPage)
              }
              disabled={currentPage === 0}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(currentPage + 1, rowsPerPage)
              }
              disabled={currentPage >= Math.ceil(totalRows / rowsPerPage) - 1}
              className="p-1 disabled:opacity-50"
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </button>
            <button
              onClick={() =>
                handlePaginationChange(
                  Math.ceil(totalRows / rowsPerPage) - 1,
                  rowsPerPage
                )
              }
              disabled={currentPage >= Math.ceil(totalRows / rowsPerPage) - 1}
              className="p-1 disabled:opacity-50"
            >
              <LastPageIcon fontSize="small" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EnquiryList;
